import * as React from "react";
import { useForm, Controller } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { ChevronUp, ChevronDown, RefreshCw, Undo2 } from "lucide-react";

import { cn } from "@/v2/lib/utils";
import { Button } from "@/v2/components/ui/button";
import { Input } from "@/v2/components/ui/input";
import { Label } from "@/v2/components/ui/label";
import { NarrativeField, type NarrativeFieldHandle } from "./NarrativeField";
import { ComboBox } from "@/v2/components/ui/combobox";
import { DatePicker } from "@/v2/components/date-picker/DatePicker";
import {
	renderTaskItem,
	renderActivityItem,
	renderMatterItem,
	renderSelectedMatter,
} from "./pickerItems";
import { getRecentMatters, searchMatters } from "@/shared/utils/matterSearch";
import {
	useMattersCache,
	useTasksCache,
	useActivitiesCache,
	useAbbreviationsCache,
	useSettingsCache,
	useRestrictedWords,
} from "@/shared/cache";
import { expandAbbreviationAtCursor } from "@/shared/utils/abbreviations";
import { useApi } from "@/shared/utils/request";
import { toast } from "sonner";
import type { Matter, Task, Activity } from "@/shared/cache";
import {
	detectRestrictedWords,
	mergeOverlappingMatches,
} from "@/shared/utils/restrictedWords";
import { type SearchableField } from "@/shared/utils/smartSearch";
import {
	isActivityRequired,
	isTaskRequired,
	isNamanHowell,
} from "@/shared/utils/firm";
import {
	availableTasksForMatter,
	availableActivitiesForMatter,
} from "@/shared/utils/firmCodes";
import {
	isZurichMatter,
	isValidUnitsForMatter,
	snapUnitsForMatter,
	stepUnitsUp,
	stepUnitsDown,
	ZURICH_UNITS_ERROR,
	STANDARD_UNITS_ERROR,
} from "@/shared/utils/zurich";

import {
	timeEntrySchema,
	type TimeEntryFormValues,
	defaultFormValues,
} from "./schema";

// ── Types ──────────────────────────────────────────────────────────

export type EntryFormHandle = {
	submitExport: () => void;
};

export type EntryFormProps = {
	/** HTML id for the form element — allows external buttons to submit via form="id" */
	formId?: string;
	defaultValues?: Partial<TimeEntryFormValues>;
	onSubmit: (values: TimeEntryFormValues) => void;
	onExport?: (values: TimeEntryFormValues) => void;
	documentName?: string;
	validateOnMount?: boolean;
	readOnly?: boolean;
	unitsReadOnly?: boolean;
	/** When set, the group's timer is running: locks units + date and shows this note. */
	activeTimerReadOnlyNote?: React.ReactNode;
	/** Banners displayed above the form fields (e.g. feedback, export errors) */
	banners?: React.ReactNode;
	/** Optional content displayed next to the narrative field. */
	narrativeSlot?: React.ReactNode;
	/** Optional action displayed where the default "Generate with AI" button appears. */
	narrativeActionSlot?: React.ReactNode;
	/** Optional action displayed alongside the units label. */
	unitsActionSlot?: React.ReactNode;
	/** Hide the generic AI narrative button when a host provides a more specific narrative action. */
	hideGenerateNarrativeButton?: boolean;
	/** Optional narrative regeneration implementation for hosts with custom endpoints. */
	onGenerateNarrative?: () => Promise<string>;
};

// ── Component ──────────────────────────────────────────────────────

function DatePickerField({
	value,
	onChange,
}: {
	value?: Date;
	onChange: (value: Date) => void;
}) {
	const selectedDate = value ?? new Date();

	return (
		<DatePicker
			date={selectedDate}
			onDateChange={onChange}
			selectionMode="single"
		/>
	);
}

export const EntryForm = React.forwardRef<EntryFormHandle, EntryFormProps>(
	function EntryForm(
		{
			formId = "entry-form",
			defaultValues: defaultValuesProp,
			onSubmit,
			onExport,
			documentName,
			validateOnMount = false,
			readOnly = false,
			unitsReadOnly: unitsReadOnlyProp = false,
			activeTimerReadOnlyNote,
			banners,
			narrativeSlot,
			narrativeActionSlot,
			unitsActionSlot,
			hideGenerateNarrativeButton = false,
			onGenerateNarrative,
		},
		ref,
	) {
		// A running group timer locks the temporal/duration fields; units also stay
		// read-only for any of their existing reasons.
		const timerLocked = Boolean(activeTimerReadOnlyNote);
		const unitsReadOnly = unitsReadOnlyProp || timerLocked;

		const { matters, isLoading: mattersLoading } = useMattersCache();
		const { tasks } = useTasksCache();
		const { activities } = useActivitiesCache();
		const { abbreviationMap, enabled: abbreviationsEnabled } =
			useAbbreviationsCache();

		const {
			handleSubmit,
			control,
			watch,
			setValue,
			setError,
			getValues,
			clearErrors,
			trigger,
			formState: { errors },
		} = useForm<TimeEntryFormValues>({
			resolver: zodResolver(timeEntrySchema),
			defaultValues: {
				...defaultFormValues,
				date: new Date(),
				...defaultValuesProp,
			},
		});

		const matterId = watch("matter_id");
		const taskValue = watch("task");
		const activityValue = watch("activity");
		const duration = watch("duration");
		const description = watch("description");

		const { settings } = useSettingsCache();
		const { getRestrictedWords } = useRestrictedWords();

		const restrictedWords = React.useMemo(() => {
			if (!settings.check_for_red_words) return [];
			if (!matterId) return getRestrictedWords();
			const clientCode = matterId.split(".")[0];
			const matterCode = matterId.split(".").slice(1).join(".");
			return getRestrictedWords(clientCode, matterCode);
		}, [settings.check_for_red_words, matterId, getRestrictedWords]);

		const { makeApiRequest } = useApi();

		const handleGenerate = React.useCallback(async (): Promise<string> => {
			if (onGenerateNarrative) return onGenerateNarrative();

			const desc = getValues("description");
			const date = getValues("date");

			if (!desc || desc.trim().length < 20) {
				toast.error("Please enter at least 20 characters before generating.");
				throw new Error("Description too short");
			}

			const response = await makeApiRequest({
				endpoint: "/records/generateNewDescription",
				method: "POST",
				body: { description: desc, date },
			});

			if (
				!response.data ||
				typeof response.data !== "string" ||
				!response.data.trim()
			) {
				toast.error("Failed to generate description. Please try manually.");
				throw new Error("Invalid AI response");
			}

			return response.data;
		}, [getValues, makeApiRequest, onGenerateNarrative]);

		// Clear validation errors when watched fields change
		React.useEffect(() => {
			if (errors.matter_id && matterId) clearErrors("matter_id");
		}, [matterId]); // eslint-disable-line react-hooks/exhaustive-deps

		React.useEffect(() => {
			if (errors.duration) clearErrors("duration");
		}, [duration]); // eslint-disable-line react-hooks/exhaustive-deps

		React.useEffect(() => {
			if (errors.task) clearErrors("task");
		}, [taskValue]); // eslint-disable-line react-hooks/exhaustive-deps

		React.useEffect(() => {
			if (errors.activity) clearErrors("activity");
		}, [activityValue]); // eslint-disable-line react-hooks/exhaustive-deps

		// ── Derived data ───────────────────────────────────────────────

		const taskRequired = React.useMemo(
			() => (mattersLoading ? true : isTaskRequired(matterId, matters)),
			[matterId, matters, mattersLoading],
		);

		const activityRequired = React.useMemo(
			() => (mattersLoading ? true : isActivityRequired(matterId, matters)),
			[matterId, matters, mattersLoading],
		);

		// Clear task/activity whenever a SELECTED matter's firm rules say the code
		// isn't required. Gated on matterId so clearing the matter doesn't wipe a
		// task/activity the user already typed — null matter keeps both pickers
		// enabled and any values intentional. Defensive against mattersLoading
		// because taskRequired/activityRequired are `true` during the load window.
		React.useEffect(() => {
			if (matterId && !taskRequired && getValues("task")) {
				setValue("task", "");
			}
			if (
				matterId &&
				!activityRequired &&
				!isNamanHowell() &&
				getValues("activity")
			) {
				setValue("activity", "");
			}
		}, [matterId, taskRequired, activityRequired, setValue, getValues]);

		// Validate on mount — deferred until matters have loaded so taskRequired/activityRequired reflect settled data.
		const hasValidatedOnMount = React.useRef(false);
		React.useEffect(() => {
			if (!validateOnMount || mattersLoading || hasValidatedOnMount.current)
				return;
			hasValidatedOnMount.current = true;
			trigger(["matter_id", "duration", "description"]);
			if (taskRequired && !getValues("task")) {
				setError("task", { type: "manual", message: "Task is required" });
			}
			if (activityRequired && !getValues("activity")) {
				setError("activity", {
					type: "manual",
					message: "Activity is required",
				});
			}
		}, [mattersLoading]); // eslint-disable-line react-hooks/exhaustive-deps

		// ── Task filtering ─────────────────────────────────────────────

		const availableTasks = React.useMemo(
			() => availableTasksForMatter(tasks, matters, matterId),
			[tasks, matters, matterId],
		);

		React.useEffect(() => {
			if (!matterId || !taskValue || tasks.length === 0) return;
			if (!availableTasks.some((t) => t.task === taskValue)) {
				setValue("task", "");
				clearErrors("task");
			}
		}, [
			matterId,
			taskValue,
			tasks.length,
			availableTasks,
			setValue,
			clearErrors,
		]);

		// Deduplicate tasks
		const uniqueTasks = React.useMemo(() => {
			const seen = new Set<string>();
			const result = availableTasks.filter((t) => {
				const key = `${t.task}-${t.task_description}`;
				if (seen.has(key)) return false;
				seen.add(key);
				return true;
			});

			result.sort((a, b) => {
				const aL = a.task.startsWith("L");
				const bL = b.task.startsWith("L");
				const aE = a.task.startsWith("E");
				const bE = b.task.startsWith("E");
				if (aL && !bL) return -1;
				if (!aL && bL) return 1;
				if (aE && !bE) return 1;
				if (!aE && bE) return -1;
				return 0;
			});

			return result;
		}, [availableTasks]);

		// Only show L-codes by default
		const taskDefaultLimit = React.useMemo(() => {
			const lCodeCount = uniqueTasks.filter((t) =>
				t.task.startsWith("L"),
			).length;
			return lCodeCount || undefined;
		}, [uniqueTasks]);

		// ── Activity filtering ─────────────────────────────────────────

		const availableActivities = React.useMemo(
			() => availableActivitiesForMatter(activities, matters, matterId),
			[activities, matters, matterId],
		);

		React.useEffect(() => {
			if (!matterId || !activityValue || activities.length === 0) return;
			if (!availableActivities.some((a) => a.activity === activityValue)) {
				setValue("activity", "");
				clearErrors("activity");
			}
		}, [
			matterId,
			activityValue,
			activities.length,
			availableActivities,
			setValue,
			clearErrors,
		]);

		const setCustomError = React.useCallback(
			(field: keyof TimeEntryFormValues, message: string) => {
				setError(field, { type: "manual", message });
			},
			[setError],
		);

		// ── Duration helpers ─────────────────────────────────────────

		const selectedClient = React.useMemo(
			() => matters.find((m) => m.matter_id === matterId)?.client ?? "",
			[matters, matterId],
		);

		const handleDurationIncrement = React.useCallback(() => {
			const current =
				typeof duration === "number"
					? duration
					: parseFloat(String(duration)) || 0;
			setValue("duration", stepUnitsUp(current, selectedClient));
		}, [duration, selectedClient, setValue]);

		const handleDurationDecrement = React.useCallback(() => {
			const current =
				typeof duration === "number"
					? duration
					: parseFloat(String(duration)) || 0;
			setValue("duration", stepUnitsDown(current, selectedClient));
		}, [duration, selectedClient, setValue]);

		// ── Matter change handler ──────────────────────────────────────

		const handleMatterSelect = React.useCallback(
			(newMatterId: string) => {
				const selected = matters.find((m) => m.matter_id === newMatterId);
				if (!selected) return;

				setValue("matter_id", selected.matter_id);
				setValue("matter", selected.matter);

				// Snap duration to the new matter's grid (handles Zurich → standard and back)
				if (unitsReadOnly) return;
				const currentDuration = getValues("duration");
				if (typeof currentDuration === "number" && currentDuration > 0) {
					setValue(
						"duration",
						snapUnitsForMatter(currentDuration, selected.client),
					);
				}
			},
			[matters, setValue, unitsReadOnly, getValues],
		);

		// ── Search field configs ───────────────────────────────────────

		const matterFields: ((item: Matter) => SearchableField)[] = React.useMemo(
			() => [
				(m: Matter) => ({ value: m.matter, weight: 1.5 }),
				(m: Matter) => ({ value: m.client, weight: 1.2 }),
				(m: Matter) => ({ value: m.description, weight: 1.0 }),
			],
			[],
		);

		const taskFields: ((item: Task) => SearchableField)[] = React.useMemo(
			() => [
				(t: Task) => ({ value: t.task, weight: 1.5 }),
				(t: Task) => ({ value: t.task_description, weight: 1.0 }),
			],
			[],
		);

		const activityFields: ((item: Activity) => SearchableField)[] =
			React.useMemo(
				() => [
					(a: Activity) => ({ value: a.activity, weight: 1.5 }),
					(a: Activity) => ({ value: a.activity_description, weight: 1.0 }),
				],
				[],
			);

		// ── Form submit ────────────────────────────────────────────────

		const validateAndPrepare = React.useCallback(
			(values: TimeEntryFormValues): TimeEntryFormValues | null => {
				if (taskRequired && !values.task) {
					setCustomError("task", "Task is required");
					return null;
				}
				if (activityRequired && !values.activity) {
					setCustomError("activity", "Activity is required");
					return null;
				}
				if (
					values.matter_id &&
					values.task &&
					tasks.length > 0 &&
					!availableTasks.some((t) => t.task === values.task)
				) {
					setCustomError("task", "Task is not available for this matter");
					return null;
				}
				if (
					values.matter_id &&
					values.activity &&
					activities.length > 0 &&
					!availableActivities.some((a) => a.activity === values.activity)
				) {
					setCustomError(
						"activity",
						"Activity is not available for this matter",
					);
					return null;
				}

				if (!unitsReadOnly) {
					// Validate unit increment against the matter's grid
					const selectedMatter = matters.find(
						(m) => m.matter_id === values.matter_id,
					);
					const submitClient = selectedMatter?.client ?? "";
					if (!isValidUnitsForMatter(values.duration, submitClient)) {
						const message = isZurichMatter(submitClient)
							? ZURICH_UNITS_ERROR
							: STANDARD_UNITS_ERROR;
						setCustomError("duration", message);
						return null;
					}
				}

				const prepared = { ...values };
				prepared.date =
					prepared.date instanceof Date
						? prepared.date
						: new Date(prepared.date);

				// Only clear when a matter IS selected and its firm rules say the code
				// isn't required. Null matter keeps any user-provided codes — the
				// picker is enabled in that case so the value is intentional.
				if (values.matter_id && !taskRequired) prepared.task = "";
				if (values.matter_id && !activityRequired && !isNamanHowell())
					prepared.activity = "";

				return prepared;
			},
			[
				taskRequired,
				activityRequired,
				matters,
				setCustomError,
				unitsReadOnly,
				tasks.length,
				activities.length,
				availableTasks,
				availableActivities,
			],
		);

		const onFormSubmit = React.useCallback(
			(values: TimeEntryFormValues) => {
				// Block submission if restricted words with "block" severity are present
				if (restrictedWords.length > 0 && values.description) {
					const matches = mergeOverlappingMatches(
						detectRestrictedWords(values.description, restrictedWords),
					);
					if (matches.some((m) => m.severity === "block")) {
						setError("description", {
							message: "Please fix restricted words before submitting",
						});
						return;
					}
				}

				const prepared = validateAndPrepare(values);
				if (!prepared) return;
				onSubmit(prepared);
			},
			[validateAndPrepare, onSubmit, restrictedWords, setError],
		);

		const onFormExport = React.useCallback(
			(values: TimeEntryFormValues) => {
				if (!onExport) return;
				if (restrictedWords.length > 0 && values.description) {
					const matches = mergeOverlappingMatches(
						detectRestrictedWords(values.description, restrictedWords),
					);
					if (matches.some((m) => m.severity === "block")) {
						setError("description", {
							message: "Please fix restricted words before submitting",
						});
						return;
					}
				}
				const prepared = validateAndPrepare(values);
				if (!prepared) return;
				onExport(prepared);
			},
			[validateAndPrepare, onExport, restrictedWords, setError],
		);

		React.useImperativeHandle(
			ref,
			() => ({
				submitExport: () => handleSubmit(onFormExport)(),
			}),
			[handleSubmit, onFormExport],
		);

		// ── Cmd/Ctrl+Enter to submit ──────────────────────────────────

		React.useEffect(() => {
			const handler = (e: KeyboardEvent) => {
				if ((e.metaKey || e.ctrlKey) && e.key === "Enter") {
					e.preventDefault();
					handleSubmit(onFormSubmit)();
				}
			};
			document.addEventListener("keydown", handler);
			return () => document.removeEventListener("keydown", handler);
		}, [handleSubmit, onFormSubmit]);

		// ── Abbreviation expansion ──────────────────────────────────────

		const narrativeRef = React.useRef<HTMLTextAreaElement | null>(null);
		const narrativeActionRef = React.useRef<NarrativeFieldHandle>(null);
		const [aiState, setAiState] = React.useState({
			isGenerating: false,
			canUndo: false,
		});

		const handleNarrativeChange = React.useCallback(
			(
				e: React.ChangeEvent<HTMLTextAreaElement>,
				fieldOnChange: (value: string) => void,
				previousValue: string,
			) => {
				const nextValue = e.target.value;
				const textarea = narrativeRef.current;
				const expansion = expandAbbreviationAtCursor({
					value: nextValue,
					previousValue,
					cursorPosition: textarea?.selectionStart,
					abbreviationMap: abbreviationsEnabled ? abbreviationMap : null,
				});

				if (expansion) {
					fieldOnChange(expansion.value);

					requestAnimationFrame(() => {
						textarea?.setSelectionRange(
							expansion.cursorPosition,
							expansion.cursorPosition,
						);
					});
					return;
				}

				fieldOnChange(nextValue);
			},
			[abbreviationMap, abbreviationsEnabled],
		);

		// ── Duration input state (allow free typing, parse on blur) ────

		const [durationInput, setDurationInput] = React.useState(
			duration && duration > 0 ? String(duration) : "",
		);

		// Sync external duration changes
		React.useEffect(() => {
			if (duration > 0) {
				setDurationInput(String(duration));
			} else {
				setDurationInput("");
			}
		}, [duration]);

		const handleDurationInputChange = React.useCallback(
			(e: React.ChangeEvent<HTMLInputElement>) => {
				setDurationInput(e.target.value);
			},
			[],
		);

		const handleDurationBlur = React.useCallback(() => {
			const parsed = parseFloat(durationInput);
			if (isNaN(parsed) || parsed <= 0) {
				const fallback = snapUnitsForMatter(0, selectedClient);
				setValue("duration", fallback);
				setDurationInput(String(fallback));
				return;
			}
			const snapped = snapUnitsForMatter(parsed, selectedClient);
			setValue("duration", snapped);
			setDurationInput(String(snapped));
		}, [durationInput, selectedClient, setValue]);

		// Live validation — clear or set the duration error as the user types
		React.useEffect(() => {
			if (durationInput === "") return;
			const parsed = parseFloat(durationInput);
			if (isNaN(parsed) || parsed <= 0) return; // blur handler will deal with it
			if (!isValidUnitsForMatter(parsed, selectedClient)) {
				setError("duration", {
					type: "manual",
					message: isZurichMatter(selectedClient)
						? ZURICH_UNITS_ERROR
						: STANDARD_UNITS_ERROR,
				});
			} else {
				clearErrors("duration");
			}
		}, [durationInput, selectedClient, setError, clearErrors]);

		const handleDurationKeyDown = React.useCallback(
			(e: React.KeyboardEvent) => {
				if (e.key === "ArrowUp") {
					e.preventDefault();
					handleDurationIncrement();
				} else if (e.key === "ArrowDown") {
					e.preventDefault();
					handleDurationDecrement();
				}
			},
			[handleDurationIncrement, handleDurationDecrement],
		);

		// ── Render ─────────────────────────────────────────────────────

		return (
			<form id={formId} onSubmit={handleSubmit(onFormSubmit)} className="p-4">
				<fieldset
					disabled={readOnly}
					className="grid min-w-0 gap-4 disabled:opacity-75"
				>
					{/* Status banners (feedback, export errors) */}
					{banners}

					{/* Document name (edit mode only) */}
					{documentName && (
						<div className="grid gap-1">
							<Label>Task Title</Label>
							<p className="text-sm text-muted-foreground leading-snug wrap-break-word rounded-md bg-muted/50 px-3 py-2">
								{documentName}
							</p>
						</div>
					)}

					{/* Matter */}
					<div className="grid w-full gap-1.5">
						<Label htmlFor="matter">Matter</Label>
						<Controller
							control={control}
							name="matter_id"
							render={({ field }) => (
								<ComboBox
									items={matters}
									value={field.value || null}
									onChange={(id) => {
										field.onChange(id);
										handleMatterSelect(id);
									}}
									fields={matterFields}
									getDefaultItems={getRecentMatters}
									searchItems={searchMatters}
									getKey={(m) => m.matter_id}
									getDisplayLabel={(m) =>
										`${m.matter.trim()} | ${m.description.trim()} - ${m.client.trim()}`
									}
									getSearchValue={(m) =>
										`${m.matter.trim()} ${m.description.trim()}`
									}
									renderItem={renderMatterItem}
									renderSelected={renderSelectedMatter}
									placeholder="Select matter..."
									error={errors.matter_id?.message}
									onBlur={field.onBlur}
									clearable
									onClear={() => {
										setValue("matter_id", "");
										setValue("matter", "");
									}}
								/>
							)}
						/>
					</div>

					{/* Task + Activity */}
					<div className="grid grid-cols-1 sm:grid-cols-2 gap-4 items-start">
						<div className="grid w-full gap-1.5">
							<Label htmlFor="task">Task</Label>
							<Controller
								control={control}
								name="task"
								render={({ field }) => (
									<ComboBox
										items={uniqueTasks}
										value={(() => {
											if (!field.value) return null;
											const found = uniqueTasks.find(
												(t) => t.task === field.value,
											);
											return found
												? `${found.task}-${found.task_description}`
												: null;
										})()}
										onChange={(key) => {
											const task = uniqueTasks.find(
												(t) => `${t.task}-${t.task_description}` === key,
											);
											field.onChange(task?.task ?? "");
										}}
										fields={taskFields}
										getKey={(t) => `${t.task}-${t.task_description}`}
										getDisplayLabel={(t) => `${t.task} - ${t.task_description}`}
										getSearchValue={(t) => t.task}
										renderItem={renderTaskItem}
										renderSelected={renderTaskItem}
										placeholder="Select task..."
										// Only disable when a matter IS selected and its firm rules
										// say a task isn't required — null matter keeps the picker
										// enabled so the user can still pick one optionally.
										disabled={!!matterId && !taskRequired}
										disabledMessage="Not required"
										defaultLimit={taskDefaultLimit}
										clearable
										onClear={() => field.onChange("")}
										error={
											taskRequired && errors.task?.message
												? errors.task.message
												: undefined
										}
										onBlur={field.onBlur}
									/>
								)}
							/>
						</div>

						<div className="grid w-full gap-1.5">
							<Label htmlFor="activity">Activity</Label>
							<Controller
								control={control}
								name="activity"
								render={({ field }) => (
									<ComboBox
										items={availableActivities}
										value={(() => {
											if (!field.value) return null;
											const found = availableActivities.find(
												(a) => a.activity === field.value,
											);
											return found
												? `${found.activity}-${found.activity_description}`
												: null;
										})()}
										onChange={(key) => {
											const act = availableActivities.find(
												(a) =>
													`${a.activity}-${a.activity_description}` === key,
											);
											field.onChange(act?.activity ?? "");
										}}
										fields={activityFields}
										getKey={(a) => `${a.activity}-${a.activity_description}`}
										getDisplayLabel={(a) =>
											`${a.activity} - ${a.activity_description}`
										}
										getSearchValue={(a) => a.activity}
										renderItem={renderActivityItem}
										renderSelected={renderActivityItem}
										placeholder="Select activity..."
										// Only disable when a matter IS selected and its firm rules
										// say activity isn't required. Naman Howell exception kept.
										disabled={
											!!matterId && !activityRequired && !isNamanHowell()
										}
										disabledMessage="Not required"
										clearable
										onClear={() => field.onChange("")}
										error={
											activityRequired && errors.activity?.message
												? errors.activity.message
												: undefined
										}
										onBlur={field.onBlur}
									/>
								)}
							/>
						</div>
					</div>

					{/* Date + Duration */}
					<div className="grid grid-cols-1 sm:grid-cols-2 gap-4 items-start">
						<div className="grid w-full gap-1.5">
							<div className="flex min-h-5 items-center justify-between gap-2">
								<Label id="entry-date-label">Date</Label>
							</div>
							<Controller
								control={control}
								name="date"
								render={({ field }) =>
									timerLocked ? (
										<p
											aria-labelledby="entry-date-label"
											className="flex h-10 items-center rounded-md bg-muted/50 px-3 text-base text-muted-foreground"
										>
											{field.value
												? new Date(field.value).toLocaleDateString()
												: ""}
										</p>
									) : (
										<DatePickerField
											value={field.value}
											onChange={(day) => field.onChange(day)}
										/>
									)
								}
							/>
							{timerLocked ? (
								<p className="mt-1 text-xs text-muted-foreground">
									{activeTimerReadOnlyNote}
								</p>
							) : null}
							{errors.date && (
								<p className="text-xs text-red-500">{errors.date.message}</p>
							)}
						</div>

						<div className="grid w-full gap-1.5">
							<div className="flex min-h-5 items-center justify-between gap-2">
								<Label id="entry-units-label" htmlFor={unitsReadOnly ? undefined : "duration"}>Units</Label>
								{unitsActionSlot}
							</div>
							{unitsReadOnly ? (
								<p
									aria-labelledby="entry-units-label"
									className="flex h-10 items-center rounded-md bg-muted/50 px-3 text-base text-muted-foreground"
								>
									{durationInput || "0"}
								</p>
							) : (
								<div className="flex gap-1">
									<Input
										id="duration"
										type="text"
										inputMode="decimal"
										autoComplete="off"
										value={durationInput}
										onChange={handleDurationInputChange}
										onBlur={handleDurationBlur}
										onKeyDown={handleDurationKeyDown}
										className={cn(
											"text-foreground bg-card border-border",
											errors.duration && "border-red-500",
										)}
									/>
									<div className="flex flex-col gap-0.5">
										<Button
											type="button"
											variant="outline"
											size="icon"
											onClick={handleDurationIncrement}
											className="h-4.5 w-7 p-0 text-foreground bg-card border-border hover:bg-muted"
											tabIndex={-1}
										>
											<ChevronUp className="h-3 w-3" />
										</Button>
										<Button
											type="button"
											variant="outline"
											size="icon"
											onClick={handleDurationDecrement}
											className="h-4.5 w-7 p-0 text-foreground bg-card border-border hover:bg-muted"
											tabIndex={-1}
										>
											<ChevronDown className="h-3 w-3" />
										</Button>
									</div>
								</div>
							)}
							{timerLocked ? (
								<p className="mt-1 text-xs text-muted-foreground">
									{activeTimerReadOnlyNote}
								</p>
							) : null}
							{errors.duration && (
								<p className="text-xs text-red-500">
									{errors.duration.message}
								</p>
							)}
						</div>
					</div>

					{/* Narrative */}
					<div className="grid w-full gap-1.5">
						<div className="flex items-center justify-between">
							<div className="flex min-w-0 items-center gap-2">
								<Label htmlFor="description">Narrative</Label>
								{narrativeSlot ? <div>{narrativeSlot}</div> : null}
							</div>
							<div className="flex items-center gap-1">
								{aiState.canUndo && (
									<Button
										type="button"
										variant="ghost"
										size="sm"
										className="h-6 text-xs gap-1 px-2 text-muted-foreground"
										onClick={() => narrativeActionRef.current?.undo()}
									>
										<Undo2 className="size-3" />
										Undo
									</Button>
								)}
								{narrativeActionSlot}
								{!hideGenerateNarrativeButton && (
									<Button
										type="button"
										variant="ghost"
										size="sm"
										className="h-6 text-xs gap-1 px-2"
										disabled={
											(!onGenerateNarrative &&
												(!description || description.trim().length < 20)) ||
											aiState.isGenerating
										}
										onClick={() => narrativeActionRef.current?.generate()}
									>
										<RefreshCw
											className={cn("size-3", aiState.isGenerating && "animate-spin")}
										/>
										{aiState.isGenerating ? "Regenerating..." : "Regenerate"}
									</Button>
								)}
							</div>
						</div>
						<Controller
							control={control}
							name="description"
							render={({ field }) => (
								<NarrativeField
									value={field.value}
									onChange={(e) =>
										handleNarrativeChange(e, field.onChange, field.value)
									}
									onBlur={field.onBlur}
									error={errors.description?.message}
									restrictedWords={restrictedWords}
									textareaRef={narrativeRef}
									onGenerate={handleGenerate}
									onDirectChange={(val) =>
										setValue("description", val, { shouldDirty: true })
									}
									actionRef={narrativeActionRef}
									onStateChange={setAiState}
								/>
							)}
						/>
					</div>
				</fieldset>
			</form>
		);
	},
);

export default EntryForm;
